import type { ReactElement } from 'react';
import { useState, useRef } from 'react';
import { scaleLinear, scaleTime } from '@visx/scale';
import type { ScaleLinear, ScaleTime } from 'd3-scale';
import XAxis from '../XAxis';
import YAxis from '../YAxis';
import Key from '../Key';
import Markers from '../Markers';
import useWindowSize from '@/hooks/useWindowSize';
import { useTheme } from '@simplywallst/ui-core';
import { Chart, ChartWrapper, Wrapper, Legend } from './styled';
import type { PeriodMap } from '@/utilities/timeSeriesPeriodMap';
import type { Entities, SeriesFilter } from '../../index';
import { useChartDefinitionsContext } from '@/components/ChartDefinitionsContext';
import { useServerEnv, Size } from '@/hooks/useServerEnv';
import useIsomorphicLayoutEffect from '@/hooks/useIsomorphicLayoutEffect';

interface DataTableProps {
  key: number;
}
interface MarkerProps {
  key: number;
  xScale: ScaleTime<number, number>;
  yScale: ScaleLinear<number, number>;
}
interface ChildrenProps<T extends string | number> {
  keys: number[];
  entities: Entities<T>;
  xScale: ScaleTime<number, number>;
  yScale: ScaleLinear<number, number>;
  minimumX: number;
  maximumX: number;
  minimumY: number;
  maximumY: number;
  zeroX: number;
  chartWidth: number;
  chartHeight: number;
}
export interface NowMarkerProps {
  x: number;
  height: number;
  hover: boolean;
}

type KeyLabelSeries = {
  key: string;
  label: string;
};
interface Props<T extends string | number> {
  id?: string;
  series: KeyLabelSeries[] | string[];
  keys: number[];
  filter: SeriesFilter<T>;
  disabled?: Partial<SeriesFilter<T>>;
  onLegendClick?: (series: string) => void;
  entities: Entities<T>;
  minimumX: number;
  maximumX: number;
  minimumY: number;
  maximumY: number;
  hasTrailingYear?: boolean;
  moveWithTrailingYear?: boolean;
  pastLabel?: string;
  futureLabel?: string;
  periods: PeriodMap;
  markerColor?: Record<string, string>;
  formatYAxis?: (value: number) => string;
  seriesColorSelect?: (series: string) => string;
  children?: (
    props: ChildrenProps<T>
  ) => ReactElement<ChildrenProps<T>>[] | ReactElement<ChildrenProps<T>>;
  dataTable?: (
    props: DataTableProps
  ) => ReactElement<DataTableProps>[] | ReactElement<DataTableProps>;
  marker?: (
    props: MarkerProps
  ) => ReactElement<MarkerProps>[] | ReactElement<MarkerProps>;
  legend?: () => ReactElement[] | ReactElement;
  nowMarker?: (props: NowMarkerProps) => ReactElement<NowMarkerProps>;
}

const padding = 32;
const paddingTop = 24;
const paddingBottom = 32;
const getSafeY = (n: number) => {
  if (Math.abs(n) !== Infinity && isNaN(n) === false) {
    return n;
  }
  return 0;
};

function seriesIsKeyLabelSeries(
  series: KeyLabelSeries | string
): series is KeyLabelSeries {
  return typeof series !== 'string';
}

function getKeys(series: KeyLabelSeries[] | string[]) {
  return series.map((n: KeyLabelSeries | string) => {
    if (seriesIsKeyLabelSeries(n)) {
      return n.key;
    }
    return n;
  });
}
function TimeSeries<T extends string | number>({
  id,
  children,
  dataTable,
  disabled,
  entities,
  filter,
  formatYAxis,
  futureLabel,
  keys,
  legend,
  marker,
  maximumX,
  maximumY,
  minimumX,
  minimumY,
  onLegendClick,
  pastLabel,
  periods,
  series,
  seriesColorSelect,
  hasTrailingYear = true,
  moveWithTrailingYear = false,
  nowMarker,
}: Props<T>): ReactElement<Props<T>> {
  const theme = useTheme();
  const { actualBackgroundGradient } = useChartDefinitionsContext();
  const serverEnv = useServerEnv();
  const breakPoints = {
    [Size.SM]: 379,
    [Size.LG]: 789,
  };
  const [width, setWidth] = useState(
    serverEnv.isServer ? breakPoints[serverEnv.size] : 0
  );
  const [height, setHeight] = useState(300);
  const SVGRef = useRef<HTMLDivElement>(null);
  const size = useWindowSize();

  const yScale: ScaleLinear<number, number> = scaleLinear({
    range: [height + paddingTop, paddingTop],
    domain: [minimumY, maximumY],
    nice: true,
  });

  const xScale: ScaleTime<number, number> = scaleTime({
    range: [padding, width],
    domain: [minimumX, maximumX],
  });

  const safeMinimumY = getSafeY(yScale.domain()[0]);
  const safeMaximumY = getSafeY(yScale.domain()[1]);

  const zeroFormatted = formatYAxis && keys.length ? formatYAxis(0) : '';

  const minimumYFormatted =
    formatYAxis && keys.length ? formatYAxis(safeMinimumY) : '';
  const maximumYFormatted =
    formatYAxis && keys.length ? formatYAxis(safeMaximumY) : '';

  useIsomorphicLayoutEffect(() => {
    const { current } = SVGRef;
    if (current) {
      const width = current.getBoundingClientRect().width;
      if (width > 0) {
        setWidth(width - padding);
      }
      if ((size.width || theme.mdMinRaw) < theme.mdMinRaw && height !== 240) {
        setHeight(240);
      }
      if ((size.width || theme.mdMinRaw) > theme.mdMinRaw && height !== 300) {
        setHeight(300);
      }
    }
  }, [size, theme.mdMinRaw, height]);

  const keysFromSeries = getKeys(series);

  return (
    <Wrapper ref={SVGRef} id={id}>
      <ChartWrapper>
        <Markers
          dataTable={dataTable}
          futureLabel={futureLabel}
          height={height}
          keys={keys}
          hasTrailingYear={hasTrailingYear}
          moveWithTrailingYear={moveWithTrailingYear}
          nowMarker={nowMarker}
          paddingBottom={paddingBottom}
          paddingTop={paddingTop}
          pastLabel={pastLabel}
          periods={periods}
          width={width}
          xScale={xScale}
        >
          {({ index }) => {
            const key = keys[index === null ? -1 : index];
            if (marker === undefined) {
              const entity = entities[key];
              const x = xScale(key);

              return (
                entity &&
                keysFromSeries
                  .filter((n) => filter[n] && entity[n] !== undefined)
                  .map((n) => (
                    <circle
                      key={`data-point-marker-${n}`}
                      transform={`translate(${x} ${yScale(entity[n])})`}
                      r="4"
                      fill={seriesColorSelect ? seriesColorSelect(n) : 'white'}
                      stroke={theme.color.white}
                      strokeWidth="1"
                    />
                  ))
              );
            }
            return marker({ key, xScale, yScale });
          }}
        </Markers>
        <Chart width="100%" height={paddingTop + height + paddingBottom}>
          {keys.length && (
            <rect
              x={xScale(minimumX)}
              y={paddingTop}
              height={height}
              width={Math.abs(
                (xScale(periods.NOW) as number) - (xScale(minimumX) as number)
              )}
              fill={actualBackgroundGradient}
              fillOpacity={0.4}
            />
          )}
          <XAxis
            xScale={xScale}
            y={paddingTop}
            height={height + paddingTop}
            rotateLabels={(size.width || theme.smMinRaw) < theme.smMinRaw}
          />
          <YAxis
            yScale={yScale}
            height={height + paddingTop}
            zeroFormatted={zeroFormatted}
            minimumFormatted={minimumYFormatted}
            maximumFormatted={maximumYFormatted}
            paddingRight={
              (size.width || theme.mdMinRaw) < theme.mdMinRaw ||
              serverEnv.isServer
                ? 8
                : 0
            }
          />
          {keys.length &&
            children &&
            children({
              keys,
              entities,
              xScale,
              yScale,
              minimumX,
              maximumX,
              minimumY: safeMinimumY,
              maximumY: safeMaximumY,
              zeroX: periods.NOW,
              chartWidth: width,
              chartHeight: height,
            })}
        </Chart>
      </ChartWrapper>
      <Legend data-cy-id="timeseries-legend">
        {legend
          ? legend()
          : series.map((n: KeyLabelSeries | string) => {
              let key: string;
              let label: string;
              if (seriesIsKeyLabelSeries(n)) {
                key = n.key;
                label = n.label;
              } else {
                key = n;
                label = n;
              }
              const disable = disabled !== undefined ? disabled[key] : true;
              return (
                <Key
                  key={`timeseries-key-${key}`}
                  disabled={disable}
                  color={
                    seriesColorSelect !== undefined
                      ? seriesColorSelect(key)
                      : 'white'
                  }
                  active={filter[key] && !disable}
                  onClick={() => {
                    onLegendClick && onLegendClick(key);
                  }}
                >
                  {label}
                </Key>
              );
            })}
      </Legend>
    </Wrapper>
  );
}

export default TimeSeries;
